// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Badge, Box, Button, Center, Group, SimpleGrid, Skeleton, Stack, Text } from '@mantine/core';
import { IconAdjustments, IconPackage, IconUsers, IconX } from '@tabler/icons';
import { FC, useContext } from 'react';
import { closeAllModals } from '@mantine/modals';
import { i18n } from '@lingui/core';
import PanePaper from '../../../Molecules/PanePaper';
import { ChargebeeS } from '../../../Service/ChargebeeS';
import { AuthContext } from '../../../Service/Context/AuthContext';
import { ResourceAddonContext } from '../../../Service/Context/ResourceAddonsContext';
import { BookitupSubscriptionContext } from '../../../Service/Context/BookitupSubscriptionContext';
import { AddonDTO } from '../../../Types/Billing';
import { DataS } from '../../../Service/DataS';
import MigrateSubscriptionAlert from './MigrateSubscriptonAlert';
import FutureSubscriptionAlert from './FutureSubscriptionAlert';
import CancelSubscriptionButton from './CancelSubscriptionButton';
import { openCancelSubscriptionModal } from './BillingModals';
import { ThankYouCitation } from './ThankYou';

const normalizeAddonName = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, '');

const RESOURCE_ADDON_NAMES = ['Resource-Planning', 'Ressourcen', 'RESOURCE'];

const isResourceAddonName = (name: string) =>
  RESOURCE_ADDON_NAMES.some((addonName) => normalizeAddonName(name).startsWith(normalizeAddonName(addonName)));

const isTeamAddonName = (name: string) => normalizeAddonName(name) === 'team';

const getAddonDefinition = (name: string) => {
  const normalizedName = normalizeAddonName(name);

  return DataS.billingAddons.find(
    (addon) =>
      normalizedName.startsWith(normalizeAddonName(addon.name)) ||
      normalizedName.startsWith(normalizeAddonName(addon.internalName)) ||
      (isResourceAddonName(name) && isResourceAddonName(addon.name)),
  );
};

const getAddonUnitLabel = (addon: AddonDTO) => getAddonDefinition(addon.name)?.price.unitLabel ?? 'Einheiten';

const getAddonDisplayName = (addon: AddonDTO) => {
  if (isResourceAddonName(addon.name)) return 'Resource Planning';
  return getAddonDefinition(addon.name)?.name ?? addon.name;
};

const AddonIcon: FC<{ name: string }> = ({ name }) => {
  if (isTeamAddonName(name)) return <IconUsers size={12} />;
  return <IconPackage size={12} />;
};

const DetailItem: FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box
    sx={(theme) => ({
      minWidth: 0,
      padding: '10px 12px',
      borderRadius: 8,

      backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
    })}
  >
    <Text size={10} color="dimmed" transform="uppercase" weight={700} sx={{ lineHeight: 1.25 }}>
      {label}
    </Text>
    <Text
      size="sm"
      weight={700}
      sx={{
        marginTop: 3,
        lineHeight: 1.25,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </Text>
  </Box>
);

const StatusIndicator: FC<{ status: string; label: string }> = ({ status, label }) => {
  const color = getStatusColor(status);

  return (
    <Badge
      size="sm"
      radius="sm"
      variant="light"
      color={color}
      leftSection={
        <Box
          sx={(theme) => ({
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors[color][3] : theme.colors[color][6],
          })}
        />
      }
      sx={(theme) => ({
        height: 24,
        paddingLeft: 8,
        paddingRight: 9,
        textTransform: 'none',
        color: theme.colorScheme === 'dark' ? theme.colors[color][2] : theme.colors[color][8],
        '.mantine-Badge-leftSection': {
          display: 'flex',
          alignItems: 'center',
          marginRight: 5,
        },
        '.mantine-Badge-inner': {
          textTransform: 'none',
          lineHeight: 1,
          fontWeight: 700,
        },
      })}
    >
      {label}
    </Badge>
  );
};

const AddonPreview: FC<{ addon: AddonDTO }> = ({ addon }) => {
  const addonName = getAddonDisplayName(addon);
  const unitLabel = getAddonUnitLabel(addon);

  return (
    <Badge
      size="xs"
      radius="sm"
      variant="outline"
      color="gray"
      leftSection={<AddonIcon name={addonName} />}
      sx={(theme) => ({
        maxWidth: '100%',
        height: 22,
        paddingLeft: 6,
        paddingRight: 8,
        textTransform: 'none',
        borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2],
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
        color: theme.colorScheme === 'dark' ? theme.colors.gray[2] : theme.colors.gray[7],
        '.mantine-Badge-leftSection': {
          display: 'flex',
          alignItems: 'center',
          marginRight: 4,
          color: isTeamAddonName(addonName) ? theme.colors.blue[5] : theme.colors.orange[5],
        },
        '.mantine-Badge-inner': {
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          textTransform: 'none',
        },
      })}
    >
      <Text span weight={700} size={10} sx={{ textTransform: 'none' }}>
        {addonName}
      </Text>
      <Text span size={10} color="dimmed" sx={{ textTransform: 'none' }}>
        {' '}
        ({addon.quantity} {unitLabel})
      </Text>
    </Badge>
  );
};

const ActiveAddonsPreview: FC<{ addons: AddonDTO[] }> = ({ addons }) => {
  if (addons.length === 0) return null;

  return (
    <Stack spacing={8}>
      <Text size={10} weight={800} color="dimmed" transform="uppercase">
        Aktive Add-ons
      </Text>
      <Group spacing={6} sx={{ minWidth: 0 }}>
        {addons.map((addon) => (
          <AddonPreview key={addon.name} addon={addon} />
        ))}
      </Group>
    </Stack>
  );
};

const getPrimaryDateLabel = (cancelledAt: string | undefined, nextBillingDate: string | undefined) => {
  if (cancelledAt) return 'Endet am';
  if (nextBillingDate) return 'Nächste Zahlung';
  return 'Startet am';
};

const getStatusColor = (status: string) => {
  if (status === 'ACTIVE' || status === 'FUTURE') return 'green';
  if (status === 'CANCELLED' || status === 'NON_RENEWING') return 'red';
  return 'gray';
};

const SubscriptionPane = () => {
  const { refreshToken } = useContext(AuthContext);
  const { loading, subscription, refresh: refreshSubscription } = useContext(BookitupSubscriptionContext);
  const { refresh: refreshAddon } = useContext(ResourceAddonContext);

  if (loading) {
    return <Skeleton radius="sm" height={104} width="100%" />;
  }

  if (!subscription) return null;

  const {
    billingInterval,
    licence,
    activatedAt,
    nextBillingDate,
    cancelledAt,
    status,
    withChargebee,
    startDate,
    addons,
  } = subscription;

  const openPortal = () => {
    const onClose = () => {
      refreshToken();
      refreshSubscription();
      refreshAddon();
    };
    ChargebeeS.openPortal(onClose);
  };

  const planName = i18n._(`subscription.licence.${licence}`);
  const primaryDateLabel = getPrimaryDateLabel(cancelledAt, nextBillingDate);
  const primaryDate = cancelledAt ?? nextBillingDate ?? startDate;
  const statusLabel = i18n._(`subscription.status.${status}`);
  const showActions = withChargebee || status === 'ACTIVE';
  const lifecycleDate = activatedAt && status !== 'FUTURE' ? activatedAt : startDate;
  const lifecycleLabel = activatedAt && status !== 'FUTURE' ? 'Aktiv seit' : 'Startet am';

  return (
    <Center>
      <Stack spacing="xs" mb="xl" sx={{ width: '100%' }}>
        {!withChargebee && <MigrateSubscriptionAlert refreshSubscription={refreshSubscription} />}
        {status === 'FUTURE' && <FutureSubscriptionAlert startDate={startDate} />}
        <PanePaper
          title="Abonnement"
          sx={(theme) => ({
            overflow: 'hidden',
            border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]}`,
          })}
        >
          <Stack spacing="md">
            <Group
              position="apart"
              align="flex-start"
              spacing="sm"
              sx={(theme) => ({
                paddingBottom: 16,
                borderBottom: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]}`,
                '@media (max-width: 576px)': {
                  alignItems: 'stretch',
                  flexDirection: 'column',
                },
              })}
            >
              <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
                <Text size={10} color="dimmed" transform="uppercase" weight={800} sx={{ lineHeight: 1.25 }}>
                  Aktueller Plan
                </Text>
                <Group
                  spacing={8}
                  mt={4}
                  noWrap
                  sx={{
                    minWidth: 0,
                    '@media (max-width: 576px)': {
                      alignItems: 'flex-start',
                      flexDirection: 'column',
                    },
                  }}
                >
                  <Text
                    size="lg"
                    weight={800}
                    sx={{
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {planName}
                  </Text>
                  <StatusIndicator status={status} label={statusLabel} />
                </Group>
              </Box>
              {showActions && (
                <Group
                  spacing={6}
                  position="right"
                  sx={{
                    flex: '0 0 auto',
                    paddingTop: 2,
                    '@media (max-width: 576px)': {
                      width: '100%',
                      justifyContent: 'flex-start',
                    },
                  }}
                >
                  {withChargebee && (
                    <Button size="xs" variant="outline" leftIcon={<IconAdjustments size={14} />} onClick={openPortal}>
                      Verwalten
                    </Button>
                  )}
                  {status === 'ACTIVE' && !withChargebee && (
                    <CancelSubscriptionButton
                      compact
                      onCancel={() => {
                        refreshSubscription();
                        closeAllModals();
                      }}
                    />
                  )}
                  {status === 'ACTIVE' && withChargebee && (
                    <Button
                      size="xs"
                      variant="subtle"
                      color="red"
                      leftIcon={<IconX size={14} />}
                      onClick={openCancelSubscriptionModal}
                    >
                      Kündigen
                    </Button>
                  )}
                </Group>
              )}
            </Group>
            <SimpleGrid
              cols={3}
              spacing="sm"
              breakpoints={[
                { maxWidth: 720, cols: 2 },
                { maxWidth: 520, cols: 1 },
              ]}
            >
              <DetailItem label={primaryDateLabel} value={primaryDate ?? '-'} />
              <DetailItem label={lifecycleLabel} value={lifecycleDate ?? '-'} />
              <DetailItem label="Abrechnung" value={billingInterval} />
            </SimpleGrid>
            <ActiveAddonsPreview addons={addons} />
            <ThankYouCitation />
          </Stack>
        </PanePaper>
      </Stack>
    </Center>
  );
};

export default SubscriptionPane;
